import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AuditEventResponse,
  bodyBlocks,
  type BodyItem,
  isOpenOffer,
  moveInBody,
  nextOfferStates,
  type QuoteSection,
  type DocumentDiscountKind,
  documentDiscountError,
  formatMoney,
  formatPercent,
  formatQuantity,
  parseDecimal,
  quoteTotals,
  LICENCE_TYPE_CODE,
  isEditable,
  type QuoteLineResponse,
  type QuoteLookups,
  QUOTE_STATE_LABELS,
  type QuoteResponse,
  type QuoteState,
  type PreparedDocumentResponse,
  conditionsEditable,
} from '@k-management/shared';
import { type FormEvent, Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useCan, useCurrentUser } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { FilesPanel } from '../../components/FilesPanel.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { describeAuditDetails } from '../../lib/audit-format.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { ClientPicker } from './ClientPicker.js';
import { LicenceDialog } from './LicenceDialog.js';
import { LineDialog } from './LineDialog.js';
import { ChecksCard } from './ChecksCard.js';
import { CostsView } from './CostsView.js';
import { DocumentsDialog } from './DocumentsDialog.js';
import { IntegrationsView } from './IntegrationsView.js';
import { DuplicateDialog, LinkDialog, LinksView } from './LinksView.js';
import {
  quotePath,
  entryLabel,
  PhaseTag,
  quoteKeys,
  quoteLabel,
  QuoteStatePill,
  useQuoteLookups,
} from './quotes-ui.js';
import { PriceListsBar } from './PriceListsBar.js';
import { FillFromListDialog } from './FillFromListDialog.js';

type Tab = 'lines' | 'costs' | 'links' | 'data' | 'integrations' | 'documents' | 'history';

/** The quote sheet (proposal §4): lines, data of the document, history. */
export function QuotePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const canWrite = useCan('preventivatore.write');
  const canApprove = useCan('preventivatore.approve');
  const user = useCurrentUser();
  const canRegistry = useCan('anagrafica.read');
  const canLinkJobs = useCan('commesse.write');
  const canDelete = useCan('preventivatore.admin');
  const lookups = useQuoteLookups();
  const quote = useQuery({
    queryKey: quoteKeys.quote(id),
    queryFn: () => apiRequest<QuoteResponse>(`/quotes/${id}`),
  });
  const [tab, setTab] = useState<Tab>('lines');
  /** The line being written: a new one (null) or the one to change. */
  const [editingLine, setEditingLine] = useState<QuoteLineResponse | null | undefined>(undefined);
  const [removingLine, setRemovingLine] = useState<QuoteLineResponse | null>(null);
  const [licences, setLicences] = useState(false);
  const canDiscountLicences = useCan('preventivatore.discounts');
  const [approving, setApproving] = useState(false);
  const [closing, setClosing] = useState<QuoteState | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [changingConditions, setChangingConditions] = useState(false);
  const [discounting, setDiscounting] = useState<DocumentDiscountKind | null>(null);
  const [duplicating, setDuplicating] = useState(false);
  const [documents, setDocuments] = useState(false);
  // The document just prepared from a template, announced over the tabs.
  const [prepared, setPrepared] = useState<PreparedDocumentResponse | null>(null);
  const [linking, setLinking] = useState(false);
  /** The type being filled with «Compila da listino»; the section being renamed or deleted. */
  const [filling, setFilling] = useState<string | null>(null);
  const [renamingSection, setRenamingSection] = useState<QuoteSection | null>(null);
  const [removingSection, setRemovingSection] = useState<QuoteSection | null>(null);
  const canCosts = useCan('preventivatore.costs');
  const canDocuments = useCan('preventivatore.documents');
  const canDiscount = useCan('preventivatore.discounts');

  const data = quote.data;
  // The subject first (decision of 01/10/2026): open tabs say whose quote it is.
  usePageTitle(data ? [data.clientName, quoteLabel(data)].filter(Boolean).join(' · ') : null);

  // The discount on the document goes when the lines change (decision of
  // 26/09/2026): say so, unless the user removed it.
  const [discountDropped, setDiscountDropped] = useState(false);
  const previousDiscount = useRef(data?.documentDiscount ?? null);
  const removingDiscount = useRef(false);
  useEffect(() => {
    const current = data?.documentDiscount ?? null;
    if (previousDiscount.current && !current && !removingDiscount.current) {
      setDiscountDropped(true);
    }
    if (current) setDiscountDropped(false);
    removingDiscount.current = false;
    previousDiscount.current = current;
  }, [data?.documentDiscount]);

  const refresh = async (updated?: QuoteResponse) => {
    if (updated) queryClient.setQueryData(quoteKeys.quote(id), updated);
    await queryClient.invalidateQueries({ queryKey: quoteKeys.all });
  };
  const action = (path: string, body?: unknown) =>
    apiRequest<QuoteResponse>(`/quotes/${id}${path}`, { method: 'POST', body });

  const setState = useMutation({
    mutationFn: (state: QuoteState) => action('/state', { state }),
    onSuccess: async (updated) => {
      setClosing(null);
      await refresh(updated);
    },
  });
  useEffect(() => {
    if (!data) return;
    const wanted = quotePath(data);
    if (!location.pathname.startsWith(wanted)) navigate(wanted, { replace: true });
  }, [data, location.pathname, navigate]);
  const copy = useMutation({
    mutationFn: (kind: 'revision' | 'duplicate') => action(`/${kind}`),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: quoteKeys.all });
      navigate(quotePath(created));
    },
  });
  // The «light» version of a sent offer, to change only its conditions (05/10/2026).
  const conditionsVersion = useMutation({
    mutationFn: () => action('/conditions-version'),
    onSuccess: async (created) => {
      setChangingConditions(false);
      await queryClient.invalidateQueries({ queryKey: quoteKeys.all });
      navigate(quotePath(created));
    },
  });
  const removeLine = useMutation({
    mutationFn: (lineId: string) =>
      apiRequest<QuoteResponse>(`/quotes/${id}/lines/${lineId}`, { method: 'DELETE' }),
    onSuccess: async (updated) => {
      setRemovingLine(null);
      await refresh(updated);
    },
  });
  const move = useMutation({
    mutationFn: (lines: string[]) => action('/lines/reorder', { lines }),
    onSuccess: refresh,
  });
  const removeSection = useMutation({
    mutationFn: (sectionId: string) =>
      apiRequest<QuoteResponse>(`/quotes/${id}/sections/${sectionId}`, { method: 'DELETE' }),
    onSuccess: async (updated) => {
      setRemovingSection(null);
      await refresh(updated);
    },
  });
  const removeQuote = useMutation({
    mutationFn: () => apiRequest<void>(`/quotes/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: quoteKeys.all });
      navigate('/preventivatore', { replace: true });
    },
  });

  const draft = data ? isEditable(data.state) : false;
  // The commerciale approves; from the threshold up the AD (allegato B §4).
  // By role, never by being administrator (decision of 04/10/2026).
  const isCommerciale = !!data && data.owner?.id === user.id;
  const mayApprove = data?.checks.needsAdApproval ? data.you.approveOver : isCommerciale;
  const waitsValidation =
    !!data?.checks.needsValidation && data.validation.decision?.outcome !== 'granted';
  const outcomes = data ? nextOfferStates(data.state).filter((state) => state !== 'inviata') : [];
  const commands: (Command | false | undefined)[] = [
    canWrite &&
      draft && {
        key: 'line',
        label: (data?.lines.length ?? 0) === 0 ? 'Aggiungi la prima riga' : 'Nuova riga',
        icon: 'plus',
        primary: true,
        onClick: () => {
          setTab('lines');
          setEditingLine(null);
        },
      },
    canWrite &&
      draft && {
        key: 'licences',
        label: 'Licenze',
        icon: 'calc',
        onClick: () => {
          setTab('lines');
          setLicences(true);
        },
      },
    canWrite &&
      canApprove &&
      mayApprove &&
      draft && {
        key: 'approve',
        label: 'Approva e crea l’offerta',
        icon: 'check',
        // No line sold without its estimated cost (26/09/2026), no offer before the validation.
        disabled:
          (data?.lines.length ?? 0) === 0 || (data?.missingCosts ?? 0) > 0 || waitsValidation,
        onClick: () => setApproving(true),
      },
    canWrite &&
      data?.state === 'da_inviare' && {
        key: 'sent',
        label: 'Segna come inviata',
        icon: 'upload',
        primary: true,
        // Conditions that ask for the CFO: sent only after the validation (05/10/2026).
        disabled:
          setState.isPending ||
          (data.checks.needsValidation && data.validation.decision?.outcome !== 'granted'),
        onClick: () => setState.mutate('inviata'),
      },
    canWrite &&
      outcomes.length > 0 && {
        key: 'outcome',
        label: 'Esito',
        icon: 'check',
        primary: data?.state === 'inviata',
        onClick: () => setClosing(outcomes[0]!),
      },
    data &&
      (!draft || canCosts) && {
        key: 'documents',
        label: 'Documenti',
        icon: 'book',
        onClick: () => setDocuments(true),
      },
    canWrite &&
      data?.state === 'inviata' && {
        key: 'conditions',
        label: 'Cambia le condizioni',
        icon: 'edit',
        disabled: conditionsVersion.isPending,
        onClick: () => setChangingConditions(true),
      },
    canWrite &&
      data &&
      isOpenOffer(data.state) && {
        key: 'revision',
        label: 'Nuova versione',
        icon: 'edit',
        disabled: copy.isPending,
        onClick: () => copy.mutate('revision'),
      },
    canWrite &&
      data && {
        key: 'duplicate',
        label: 'Duplica',
        icon: 'plus',
        disabled: copy.isPending,
        onClick: () => setDuplicating(true),
      },
    canDelete &&
      draft && {
        key: 'delete',
        label: 'Elimina',
        icon: 'close',
        onClick: () => setDeleting(true),
      },
  ];
  useCommands(commands.filter((command): command is Command => Boolean(command)));

  if (quote.isPending || lookups.isPending) return <p className="muted">Caricamento…</p>;
  if (quote.isError) {
    return (
      <section className="stack">
        <p className="error">{quote.error.message}</p>
        <button type="button" className="link" onClick={() => navigate('/preventivatore')}>
          Torna ai preventivi
        </button>
      </section>
    );
  }
  if (!data || !lookups.data) return null;

  return (
    <section className="stack">
      <div className="page-header">
        <div className="stack tight">
          {/* The subject is the title; the rest of the quote comes under it (decision of 01/10/2026). */}
          <h1 className={data.clientName ? undefined : 'muted'}>
            {data.clientName && data.clientId && canRegistry ? (
              <Link to={`/anagrafica/${data.clientId}`} className="title-link">
                {data.clientName}
              </Link>
            ) : (
              (data.clientName ?? 'Cliente da scegliere')
            )}
          </h1>
          <div className="flags">
            <PhaseTag state={data.state} />
            <strong className="quote-name">{quoteLabel(data)}</strong>
            <QuoteStatePill state={data.state} expired={data.expired} />
            {data.number && data.referenceName && (
              <span className="muted">{data.referenceName}</span>
            )}
            {data.revisionOf && data.number && (
              <span className="pill muted">sostituisce {data.revisionOf}</span>
            )}
            {data.links
              .filter((link) => link.kind === 'origin')
              .map((link) =>
                link.quote ? (
                  <span key={link.id} className="pill muted">
                    duplicato da{' '}
                    <Link to={`/preventivatore/${link.quote.id}`}>{quoteLabel(link.quote)}</Link>
                  </span>
                ) : (
                  <span key={link.id} className="pill muted">
                    duplicato da un preventivo che non vedi
                  </span>
                ),
              )}
          </div>
          <div className="muted small subject-meta">
            <span>del {formatDate(data.date)}</span>
            {data.validUntil && <span>valida fino al {formatDate(data.validUntil)}</span>}
            <OwnerPicker quote={data} salespeople={lookups.data.salespeople} onChanged={refresh} />
            {data.compiledBy && <span>compilato da {data.compiledBy.name}</span>}
          </div>
        </div>
        {/* What K-Rev looks at is the taxable amount; the total with VAT comes second. */}
        <div className="quote-total">
          <small className="muted">Imponibile</small>
          <strong>{formatMoney(data.totals.net)}</strong>
          <small className="muted">Totale con IVA {formatMoney(data.totals.total)}</small>
        </div>
      </div>
      <PriceListsBar
        quote={data}
        lookups={lookups.data}
        canChange={canWrite && draft}
        onChanged={refresh}
      />
      {copy.isError && <p className="error">{copy.error.message}</p>}
      {draft && (
        <p className="notice info">
          {data.versionOf
            ? `È la nuova versione di ${data.versionOf.number}: un preventivo per uso interno. L’offerta resta com’è finché questo non viene approvato.`
            : 'È un preventivo per uso interno: costi e ricavi si valutano qui e non ha numero. Con l’approvazione diventa un’offerta per il cliente.'}
          {!data.clientId && ' Il cliente si sceglie al momento dell’approvazione.'}
        </p>
      )}
      {/* The conditions may still change on an offer to send, and ask for the CFO (05/10/2026). */}
      {(draft || data.state === 'da_inviare') && <ChecksCard quote={data} onChanged={refresh} />}
      {draft && data.checks.needsAdApproval && !data.you.approveOver && (
        <p className="notice info">
          {data.deciders.ad.length > 0
            ? `Da questo importo approva l’AD (${data.deciders.ad.join(', ')}): quando il preventivo è pronto, chiedigli di approvarlo.`
            : 'Da questo importo approva l’AD, ma nessun utente è AD: va assegnato in Organizzazione › Ruoli.'}
        </p>
      )}
      {!draft && isOpenOffer(data.state) && (
        <p className="notice info">
          {data.state === 'da_inviare'
            ? 'È un’offerta da inviare: righe e prezzi non si modificano, le condizioni (Dati del documento e Integrazioni) sì, fino all’invio. Per cambiare righe o prezzi crea una nuova versione, che riparte dal preventivo.'
            : 'È un’offerta: non si modifica. Se il cliente chiede cambiamenti, crea una nuova versione, che riparte dal preventivo e va riapprovata.'}
        </p>
      )}
      {/* An accepted offer goes into a commessa (16/10/2026): who changes the commesse links it. */}
      {data.job ? (
        <p className="notice ok-notice">
          Commessa{' '}
          {data.job.visible ? (
            <Link to={`/commesse/${data.job.id}`}>
              {data.job.code} · {data.job.name}
            </Link>
          ) : (
            `${data.job.code} · ${data.job.name}`
          )}
        </p>
      ) : (
        data.state === 'accettata' && (
          <p className="notice">
            Da collegare a una commessa: lo fa chi gestisce le commesse
            {canLinkJobs && (
              <>
                , dall’elenco <Link to="/commesse">Commesse</Link>
              </>
            )}
            .
          </p>
        )
      )}
      {draft && data.missingCosts > 0 && (
        <p className="notice">
          <strong>Non si può approvare:</strong>{' '}
          {data.missingCosts === 1
            ? '1 riga non ha il costo previsto'
            : `${data.missingCosts} righe non hanno il costo previsto`}
          {data.costs ? (
            <>
              {' '}
              nella scheda{' '}
              <button type="button" className="link" onClick={() => setTab('costs')}>
                Costi previsti
              </button>
              . Se un costo non c’è, scegli «Personalizzato» e metti 0.
            </>
          ) : (
            ': chiedi a chi gestisce i costi di sceglierlo.'
          )}
        </p>
      )}

      {prepared && (
        <p className="notice ok-notice row between">
          <span>
            Pronto «{prepared.name}» nella scheda «File»: completalo in Word.
            {!prepared.webUrl && ' Aprilo, salvalo e ricaricalo qui quando è completo.'}
          </span>
          <span className="row">
            {prepared.webUrl && (
              <a className="button" href={prepared.webUrl} target="_blank" rel="noreferrer">
                Apri in Word ↗
              </a>
            )}
            <button type="button" className="link" onClick={() => setPrepared(null)}>
              Chiudi
            </button>
          </span>
        </p>
      )}
      <nav className="subtabs" aria-label="Sezioni del preventivo">
        {(
          [
            ['lines', `Righe (${data.lines.length})`],
            ...(data.costs ? [['costs', 'Costi previsti']] : []),
            ['links', `Collegamenti (${data.links.length})`],
            ['data', 'Dati del documento'],
            ['integrations', 'Integrazioni'],
            ...(canDocuments && !draft ? [['documents', 'File']] : []),
            ['history', 'Storico'],
          ] as [Tab, ReactNode][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={tab === key ? 'subtab selected' : 'subtab'}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === 'lines' && (
        <LinesView
          quote={data}
          canWrite={canWrite && draft}
          onAdd={() => setEditingLine(null)}
          onEdit={(line) =>
            // Licence lines change only in the configurator, with the price list.
            line.typeCode === LICENCE_TYPE_CODE ? setLicences(true) : setEditingLine(line)
          }
          onRemove={setRemovingLine}
          onMove={(lines) => move.mutate(lines)}
          onRenameSection={setRenamingSection}
          onRemoveSection={setRemovingSection}
          {...(canWrite && draft && canDiscount ? { onDiscount: setDiscounting } : {})}
          discountDropped={discountDropped}
        />
      )}
      {discounting && lookups.data && (
        <DocumentDiscountDialog
          quote={data}
          lookups={lookups.data}
          kind={discounting}
          onClose={() => setDiscounting(null)}
          onDone={async (updated, removed) => {
            if (removed) removingDiscount.current = true;
            setDiscounting(null);
            await refresh(updated);
          }}
        />
      )}
      {tab === 'costs' && data.costs && (
        <CostsView
          quote={data}
          costs={data.costs}
          canWrite={canWrite && draft}
          onChanged={refresh}
        />
      )}
      {tab === 'links' && (
        <LinksView
          quote={data}
          canWrite={canWrite}
          onLink={() => setLinking(true)}
          onChanged={refresh}
        />
      )}
      {linking && (
        <LinkDialog
          quote={data}
          onClose={() => setLinking(false)}
          onDone={async (updated) => {
            setLinking(false);
            await refresh(updated);
          }}
        />
      )}
      {documents && (
        <DocumentsDialog
          quote={data}
          canCosts={canCosts}
          canPrepare={canDocuments && canWrite}
          onClose={() => setDocuments(false)}
          onPrepared={(result) => {
            queryClient.setQueryData([...quoteKeys.quote(id), 'files'], result.files);
            setPrepared(result);
            setDocuments(false);
            setTab('documents');
          }}
        />
      )}
      {changingConditions && data && (
        <ConfirmDialog
          title="Cambiare le condizioni dell’offerta inviata?"
          confirmLabel={`Crea la versione ${(data.version ?? 1) + 1}`}
          busy={conditionsVersion.isPending}
          error={conditionsVersion.error?.message}
          onConfirm={() => conditionsVersion.mutate()}
          onCancel={() => setChangingConditions(false)}
        >
          <p>
            Il cliente ha già la versione {data.version ?? 1}: per non confondere i documenti nasce
            la versione {(data.version ?? 1) + 1}, con le stesse righe e gli stessi prezzi, già
            approvata e da inviare. Su quella cambi pagamento, piano a rate, fatturazione, durata,
            validità e note per il cliente. La versione {data.version ?? 1} diventa «superata».
          </p>
          <p className="muted small">
            Se le nuove condizioni lo chiedono, serve la convalida del CFO prima dell’invio. Per
            cambiare righe o prezzi usa «Nuova versione».
          </p>
        </ConfirmDialog>
      )}
      {duplicating && (
        <DuplicateDialog
          quote={data}
          canCosts={canCosts}
          onClose={() => setDuplicating(false)}
          onDone={async (created) => {
            setDuplicating(false);
            await queryClient.invalidateQueries({ queryKey: quoteKeys.all });
            navigate(`/preventivatore/${created.id}`);
          }}
        />
      )}
      {tab === 'data' && <DataView quote={data} lookups={lookups.data} onChanged={refresh} />}
      {tab === 'integrations' && (
        <IntegrationsView quote={data} lookups={lookups.data} onChanged={refresh} />
      )}
      {tab === 'documents' && canDocuments && !draft && (
        <FilesPanel
          base={`/quotes/${id}/files`}
          queryKey={[...quoteKeys.quote(id), 'files']}
          canWrite={canWrite}
          pathPrefix="Soggetti"
          showVersion
          noFolder="L’offerta non ha ancora documenti: la sua cartella si crea con il primo file, dentro quella del cliente, ed è la stessa per tutte le versioni."
          removeNote="L’eliminazione resta nello storico dell’offerta."
        />
      )}
      {tab === 'history' && <HistoryView id={id} />}

      {editingLine !== undefined && (
        <LineDialog
          quote={data}
          lookups={lookups.data}
          {...(editingLine ? { line: editingLine } : {})}
          onClose={() => setEditingLine(undefined)}
          onSaved={async (updated) => {
            setEditingLine(undefined);
            await refresh(updated);
          }}
          onFill={(typeCode) => {
            // A type filled from its list opens the grid (decision of 01/10/2026).
            setEditingLine(undefined);
            setFilling(typeCode);
          }}
          onLicences={() => {
            setEditingLine(undefined);
            setLicences(true);
          }}
        />
      )}
      {filling && (
        <FillFromListDialog
          quote={data}
          lookups={lookups.data}
          typeCode={filling}
          onClose={() => setFilling(null)}
          onDone={async (updated) => {
            setFilling(null);
            await refresh(updated);
          }}
        />
      )}
      {renamingSection && (
        <SectionNameDialog
          quoteId={id}
          section={renamingSection}
          onClose={() => setRenamingSection(null)}
          onDone={async (updated) => {
            setRenamingSection(null);
            await refresh(updated);
          }}
        />
      )}
      {removingSection && (
        <ConfirmDialog
          title={`Eliminare la sezione «${removingSection.name}»?`}
          confirmLabel="Elimina la sezione"
          busy={removeSection.isPending}
          error={removeSection.error?.message}
          onConfirm={() => removeSection.mutate(removingSection.id)}
          onCancel={() => setRemovingSection(null)}
        >
          <p>
            Spariscono anche le sue righe (
            {data.lines.filter((line) => line.sectionId === removingSection.id).length}, per{' '}
            {formatMoney(removingSection.net)}) con i loro costi previsti. Resta nello storico del
            preventivo.
          </p>
        </ConfirmDialog>
      )}
      {removingLine && (
        <ConfirmDialog
          title="Eliminare la riga?"
          confirmLabel="Elimina"
          busy={removeLine.isPending}
          error={removeLine.error?.message}
          onConfirm={() => removeLine.mutate(removingLine.id)}
          onCancel={() => setRemovingLine(null)}
        >
          <p>
            «{removingLine.label}», {formatMoney(removingLine.net)}. Resta nello storico del
            preventivo.
          </p>
        </ConfirmDialog>
      )}
      {deleting && (
        <ConfirmDialog
          title={`Eliminare ${quoteLabel(data)}?`}
          confirmLabel="Elimina"
          busy={removeQuote.isPending}
          error={removeQuote.error?.message}
          onConfirm={() => removeQuote.mutate()}
          onCancel={() => setDeleting(false)}
        >
          <p>
            Il preventivo e le sue righe spariscono; l’eliminazione resta nel registro attività.
            Un’offerta invece non si elimina: si porta ad «annullata».
          </p>
        </ConfirmDialog>
      )}
      {licences && (
        <LicenceDialog
          before={data.licences}
          hasLicences={data.lines.some((line) => line.typeCode === LICENCE_TYPE_CODE)}
          canDiscount={canDiscountLicences}
          priceListPath="/quotes/price-list"
          where="preventivo"
          onClose={() => setLicences(false)}
          onSave={async (body) => {
            const updated = await apiRequest<QuoteResponse>(`/quotes/${data.id}/licences`, {
              method: 'POST',
              body,
            });
            setLicences(false);
            await refresh(updated);
          }}
        />
      )}
      {approving && (
        <ApproveDialog
          quote={data}
          canRegistry={canRegistry}
          onClose={() => setApproving(false)}
          onApproved={async (updated) => {
            setApproving(false);
            await refresh(updated);
          }}
        />
      )}
      {closing && (
        <ConfirmDialog
          title={`Esito di ${quoteLabel(data)}`}
          confirmLabel="Registra"
          busy={setState.isPending}
          error={setState.error?.message}
          onConfirm={() => setState.mutate(closing)}
          onCancel={() => setClosing(null)}
        >
          <Field label="Come è andata">
            <select
              value={closing}
              onChange={(event) => setClosing(event.target.value as QuoteState)}
            >
              {outcomes.map((state) => (
                <option key={state} value={state}>
                  {QUOTE_STATE_LABELS[state]}
                </option>
              ))}
            </select>
          </Field>
          <p className="muted small">L’esito non si torna indietro: resta nello storico.</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

/* --- Lines ------------------------------------------------------------------- */

/**
 * The lines as they are saved: one at a time, each changed with the pencil,
 * removed with the cross and moved with the arrows (decision of 23/09/2026).
 */
function LinesView({
  quote,
  canWrite,
  onAdd,
  onEdit,
  onRemove,
  onMove,
  onRenameSection,
  onRemoveSection,
  onDiscount,
  discountDropped,
}: {
  quote: QuoteResponse;
  canWrite: boolean;
  onAdd: () => void;
  onEdit: (line: QuoteLineResponse) => void;
  onRemove: (line: QuoteLineResponse) => void;
  onMove: (lineIds: string[]) => void;
  onRenameSection: (section: QuoteSection) => void;
  onRemoveSection: (section: QuoteSection) => void;
  /** Opens the discount on the document; absent for who may not set it. */
  onDiscount?: (kind: DocumentDiscountKind) => void;
  discountDropped: boolean;
}) {
  const lookups = useQuoteLookups();
  // A section moves as a whole and its lines stay inside it (proposal Listini §7).
  const arrows = (item: BodyItem, label: string) =>
    ([-1, 1] as const).map((direction) => {
      const order = moveInBody(quote.lines, item, direction);
      return (
        <button
          key={direction}
          type="button"
          className="small"
          aria-label={`Sposta ${direction < 0 ? 'su' : 'giù'} ${label}`}
          disabled={order === null}
          onClick={() => order && onMove(order)}
        >
          {direction < 0 ? '↑' : '↓'}
        </button>
      );
    });

  if (quote.lines.length === 0) {
    return (
      <section className="card stack">
        <p className="muted">
          Il preventivo non ha ancora righe: si parte dalla tipologia di quello che si vende.
        </p>
        {canWrite && (
          <div>
            <button type="button" className="primary" onClick={onAdd}>
              Aggiungi la prima riga
            </button>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="card stack">
      <table className="lines">
        <thead>
          <tr>
            <th>Tipologia</th>
            <th className="right">Edizioni</th>
            <th className="right">Q.tà</th>
            <th>U.M.</th>
            <th className="right">Prezzo</th>
            <th className="right">Sconto</th>
            <th>IVA</th>
            <th className="right">Importo</th>
            {canWrite && <th aria-label="Azioni" />}
          </tr>
        </thead>
        <tbody>
          {bodyBlocks(quote.lines).map((block) => {
            const section = block.sectionId
              ? quote.sections.find((item) => item.id === block.sectionId)
              : undefined;
            return (
              <Fragment key={block.sectionId ?? block.lines[0]!.id}>
                {section && (
                  <tr className="section-row">
                    <td colSpan={7}>
                      {section.name}{' '}
                      <small className="muted">
                        {block.lines.length === 1 ? '1 riga' : `${block.lines.length} righe`}
                      </small>
                    </td>
                    <td className="right">{formatMoney(section.net)}</td>
                    {canWrite && (
                      <td className="row-actions">
                        {arrows({ section: section.id }, `la sezione ${section.name}`)}
                        <button
                          type="button"
                          className="small"
                          aria-label={`Rinomina la sezione ${section.name}`}
                          onClick={() => onRenameSection(section)}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="small"
                          aria-label={`Elimina la sezione ${section.name}`}
                          onClick={() => onRemoveSection(section)}
                        >
                          ✕
                        </button>
                      </td>
                    )}
                  </tr>
                )}
                {block.lines.map((line) => (
                  <LineRowView
                    key={line.id}
                    line={line}
                    inSection={Boolean(section)}
                    canWrite={canWrite}
                    arrows={arrows({ line: line.id }, line.label)}
                    onEdit={onEdit}
                    onRemove={onRemove}
                  />
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {canWrite && (
        <div>
          <button type="button" onClick={onAdd}>
            Nuova riga
          </button>
        </div>
      )}
      {quote.licences && <LicenceSummaryCard licences={quote.licences} />}
      {discountDropped && (
        <p className="notice">
          Le righe sono cambiate: lo sconto sul documento è stato tolto. Se serve, impostalo di
          nuovo sull’imponibile o sul totale.
        </p>
      )}
      {lookups.data && (
        <Totals
          totals={quote.totals}
          lookups={lookups.data}
          {...(onDiscount ? { onDiscount } : {})}
        />
      )}
    </section>
  );
}

/** A line of the body: in a section it is indented and moves only inside it. */
function LineRowView({
  line,
  inSection,
  canWrite,
  arrows,
  onEdit,
  onRemove,
}: {
  line: QuoteLineResponse;
  inSection: boolean;
  canWrite: boolean;
  arrows: ReactNode;
  onEdit: (line: QuoteLineResponse) => void;
  onRemove: (line: QuoteLineResponse) => void;
}) {
  const lookups = useQuoteLookups();
  return (
    <tr className={inSection ? 'in-section' : undefined}>
      <td>
        {line.label}
        {line.isExpense && (
          <>
            {' '}
            <span className="pill info">spesa</span>
          </>
        )}
        {line.recurrence && <small className="muted block">{line.recurrence}</small>}
        {line.mergeUp && (
          <small className="muted block">
            nell’offerta per il cliente si somma alla riga sopra
          </small>
        )}
      </td>
      <td className="right">{line.editions}</td>
      <td className="right">{formatQuantity(line.quantity)}</td>
      <td>{entryLabel(lookups.data?.units, line.unitCode)}</td>
      <td className="right">
        {formatMoney(line.unitPrice)}
        {/* A price changed from the list of the quote (proposal Listini §5). */}
        {line.listPrice !== null &&
          parseDecimal(line.listPrice) !== parseDecimal(line.unitPrice) && (
            <small className="muted block" title="Prezzo cambiato per questo preventivo">
              listino {formatMoney(line.listPrice)}
            </small>
          )}
      </td>
      <td className="right">
        <LineDiscount line={line} />
      </td>
      <td>{rateLabel(lookups.data, line.vatCode)}</td>
      <td className="right">{formatMoney(line.net)}</td>
      {canWrite && (
        <td className="row-actions">
          {arrows}
          <button
            type="button"
            className="small"
            aria-label={`Modifica ${line.label}`}
            onClick={() => onEdit(line)}
          >
            ✎
          </button>
          <button
            type="button"
            className="small"
            aria-label={`Elimina ${line.label}`}
            onClick={() => onRemove(line)}
          >
            ✕
          </button>
        </td>
      )}
    </tr>
  );
}

/** Renames a section of the body: «Sviluppo» becomes «Fase 1 – Analisi». */
function SectionNameDialog({
  quoteId,
  section,
  onClose,
  onDone,
}: {
  quoteId: string;
  section: QuoteSection;
  onClose: () => void;
  onDone: (updated: QuoteResponse) => Promise<void>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [name, setName] = useState(section.name);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const save = useMutation({
    mutationFn: () =>
      apiRequest<QuoteResponse>(`/quotes/${quoteId}/sections/${section.id}`, {
        method: 'PATCH',
        body: { name: name.trim() },
      }),
    onSuccess: (updated) => onDone(updated),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim()) save.mutate();
  };
  return (
    <dialog ref={dialogRef} className="dialog narrow" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <h2>Rinomina la sezione</h2>
        <Field label="Nome della sezione" error={name.trim() ? undefined : 'Campo obbligatorio'}>
          <input
            value={name}
            maxLength={120}
            autoFocus
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        {save.isError && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button type="submit" className="primary" disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/** Lo schema delle licenze da richiamare in trattativa (deciso il 24/09/2026). */
function LicenceSummaryCard({ licences }: { licences: NonNullable<QuoteResponse['licences']> }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="licence-card">
      <button type="button" className="link" onClick={() => setOpen(!open)}>
        {open ? 'Nascondi lo schema delle licenze' : 'Schema delle licenze'}
      </button>
      {open && (
        <dl className="totals licence-summary">
          <div>
            <dt>Licenze</dt>
            <dd>{formatQuantity(licences.pax)}</dd>
          </div>
          <div>
            <dt>Costo setup</dt>
            <dd>{formatMoney(licences.summary.setup)}</dd>
          </div>
          <div>
            <dt>Licenza al mese</dt>
            <dd>{formatMoney(licences.summary.perLicenceMonth)}</dd>
          </div>
          <div>
            <dt>Licenza all’anno</dt>
            <dd>{formatMoney(licences.summary.perLicenceYear)}</dd>
          </div>
          {licences.months !== 12 && (
            <div>
              <dt>Primo periodo</dt>
              <dd>{licences.months === 1 ? '1 mese' : `${licences.months} mesi`}</dd>
            </div>
          )}
          {licences.renewalDiscount !== null && (
            <div>
              <dt>Sconto sul rinnovo</dt>
              <dd>{formatPercent(licences.renewalDiscount)}</dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

/* --- Data of the document ------------------------------------------------------ */

/**
 * The data of the quote: while it is a preventivo they are fields to fill,
 * once it is an offer they are what the client was told, kept as it was that
 * day. The conditions (payment, billing, duration, validity) go into the offer.
 */
function DataView({
  quote,
  lookups,
  onChanged,
}: {
  quote: QuoteResponse;
  lookups: QuoteLookups;
  onChanged: (updated?: QuoteResponse) => Promise<void>;
}) {
  const canWrite = useCan('preventivatore.write');
  const [name, setName] = useState(quote.referenceName ?? '');
  const [days, setDays] = useState(String(quote.validityDays));
  const [notes, setNotes] = useState(quote.notes ?? '');
  const [billing, setBilling] = useState(quote.billingNotes ?? '');
  const termsLabel = (code: string | null) =>
    code ? (lookups.paymentTerms.find((term) => term.code === code)?.description ?? code) : '—';
  const period =
    quote.startDate || quote.endDate
      ? `${quote.startDate ? formatDate(quote.startDate) : '…'} – ${quote.endDate ? formatDate(quote.endDate) : '…'}`
      : '—';
  const [error, setError] = useState('');
  const editable = canWrite && conditionsEditable(quote.state);
  // An offer to send changes only its conditions (decision of 05/10/2026).
  const offer = !isEditable(quote.state);

  const update = useMutation({
    mutationFn: (body: unknown) =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}`, { method: 'PATCH', body }),
    onSuccess: (updated) => onChanged(updated),
    onError: (mutationError: Error) => setError(mutationError.message),
  });
  /** Saves on leaving the field, and only what changed. */
  const saveOnBlur = (changed: boolean, body: Record<string, unknown>) => () => {
    if (changed) update.mutate(body);
  };

  if (!editable) {
    return (
      <section className="card stack">
        <dl className="pairs">
          <div>
            <dt>Cliente</dt>
            <dd>{quote.snapshot?.name ?? quote.clientName ?? '—'}</dd>
          </div>
          {quote.snapshot && (
            <>
              <div>
                <dt>P.IVA / C.F. all’approvazione</dt>
                <dd className="code">{quote.snapshot.vatNumber ?? '—'}</dd>
              </div>
              <div>
                <dt>Indirizzo all’approvazione</dt>
                <dd>{quote.snapshot.address ?? '—'}</dd>
              </div>
              <div>
                <dt>Termini del cliente in anagrafica</dt>
                <dd>{termsLabel(quote.snapshot.termsCode)}</dd>
              </div>
            </>
          )}
          <div>
            <dt>Oggetto</dt>
            <dd>{quote.referenceName ?? '—'}</dd>
          </div>
          <div>
            <dt>Pagamento proposto</dt>
            <dd>{termsLabel(quote.paymentTermsCode)}</dd>
          </div>
          <div>
            <dt>Fatturazione</dt>
            <dd className="pre-line">{quote.billingNotes || '—'}</dd>
          </div>
          <div>
            <dt>Durata</dt>
            <dd>{period}</dd>
          </div>
          <div>
            <dt>Penali o clausole non standard</dt>
            <dd>{quote.penalties ? 'Sì' : 'No'}</dd>
          </div>
          <div>
            <dt>Validità</dt>
            <dd>
              {quote.validityDays} giorni
              {quote.validUntil && ` · fino al ${formatDate(quote.validUntil)}`}
            </dd>
          </div>
          <div>
            <dt>Approvato</dt>
            <dd>
              {quote.approval
                ? `il ${formatTimestamp(quote.approval.at)}${quote.approval.by ? ` da ${quote.approval.by}` : ''}`
                : '—'}
            </dd>
          </div>
          <div>
            <dt>Inviata il</dt>
            <dd>{quote.sentAt ? formatTimestamp(quote.sentAt) : '—'}</dd>
          </div>
          <div>
            <dt>Note interne</dt>
            <dd className="pre-line">{quote.notes || '—'}</dd>
          </div>
        </dl>
      </section>
    );
  }

  return (
    <section className="card stack">
      {offer ? (
        <p className="notice info">
          È un’offerta da inviare: si cambiano solo le condizioni. Cliente, oggetto e righe restano
          quelli approvati.
        </p>
      ) : (
        <>
          <ClientPicker
            value={quote.clientId}
            onChange={(clientId) => update.mutate({ clientId })}
            hint="Si può scegliere anche al momento dell’approvazione."
            returnTo={`/preventivatore/${quote.id}`}
          />
          <Field label="Oggetto" hint="Il titolo dell’offerta, come lo legge il cliente.">
            <input
              value={name}
              maxLength={200}
              onChange={(event) => setName(event.target.value)}
              onBlur={saveOnBlur(name !== (quote.referenceName ?? ''), {
                referenceName: name || null,
              })}
            />
          </Field>
        </>
      )}
      <div className="row">
        <Field label="Validità dell’offerta (giorni)" hint="30 di base, contati dall’approvazione.">
          <input
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(event) => setDays(event.target.value)}
            onBlur={saveOnBlur(days !== String(quote.validityDays), { validityDays: days })}
          />
        </Field>
      </div>
      <h3>Condizioni dell’offerta</h3>
      <div className="row">
        <Field label="Pagamento proposto">
          <select
            value={quote.paymentTermsCode ?? ''}
            onChange={(event) => update.mutate({ paymentTermsCode: event.target.value || null })}
          >
            <option value="">—</option>
            {lookups.paymentTerms.map((term) => (
              <option key={term.code} value={term.code}>
                {term.description}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Inizio">
          <input
            type="date"
            value={quote.startDate ?? ''}
            onChange={(event) => update.mutate({ startDate: event.target.value || null })}
          />
        </Field>
        <Field label="Fine">
          <input
            type="date"
            value={quote.endDate ?? ''}
            onChange={(event) => update.mutate({ endDate: event.target.value || null })}
          />
        </Field>
      </div>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={quote.penalties}
          onChange={(event) => update.mutate({ penalties: event.target.checked })}
        />
        Penali o clausole non standard: il contratto è complesso e prima della firma serve il parere
        di un consulente
      </label>
      <Field
        label="Fatturazione"
        hint="Come si fattura: per esempio «30% alla firma, saldo a fine percorso»."
      >
        <textarea
          rows={2}
          maxLength={1000}
          value={billing}
          onChange={(event) => setBilling(event.target.value)}
          onBlur={saveOnBlur(billing !== (quote.billingNotes ?? ''), {
            billingNotes: billing || null,
          })}
        />
      </Field>
      {!offer && (
        <Field label="Note interne">
          <textarea
            rows={4}
            maxLength={2000}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            onBlur={saveOnBlur(notes !== (quote.notes ?? ''), { notes: notes || null })}
          />
        </Field>
      )}
      {error && <p className="error">{error}</p>}
    </section>
  );
}

function HistoryView({ id }: { id: string }) {
  const history = useQuery({
    queryKey: quoteKeys.history(id),
    queryFn: () => apiRequest<AuditEventResponse[]>(`/quotes/${id}/history`),
  });
  return (
    <section className="card">
      {history.isPending && <p className="muted">Caricamento…</p>}
      <ul className="timeline">
        {history.data?.map((event) => (
          <li key={event.id}>
            <span className="muted small">
              {formatTimestamp(event.occurredAt)}
              <br />
              {event.actorEmail ?? '—'}
            </span>
            <span>
              {event.summary}
              {describeAuditDetails(event).map((line) => (
                <small key={line} className="muted block">
                  {line}
                </small>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The approval (rule of 03/10/2026): the preventivo becomes an offer, with the
 * number OFF and its version, and closes to changes.
 */
function ApproveDialog({
  quote,
  canRegistry,
  onClose,
  onApproved,
}: {
  quote: QuoteResponse;
  canRegistry: boolean;
  onClose: () => void;
  onApproved: (updated: QuoteResponse) => Promise<void>;
}) {
  const [clientId, setClientId] = useState<string | null>(quote.clientId);
  const approve = useMutation({
    mutationFn: () =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}/approve`, {
        method: 'POST',
        body: { clientId },
      }),
    onSuccess: (updated) => onApproved(updated),
  });

  return (
    <ConfirmDialog
      title="Approvare il preventivo?"
      confirmLabel="Approva e crea l’offerta"
      busy={approve.isPending}
      error={approve.error?.message}
      onConfirm={() => clientId && approve.mutate()}
      onCancel={onClose}
    >
      <p>
        {quote.versionOf
          ? `Diventa la versione ${quote.versionOf.version} dell’offerta ${quote.versionOf.number}; la versione precedente diventa «superata».`
          : 'Diventa un’offerta per il cliente, con il suo numero OFF, versione 1.'}{' '}
        Da lì non si modifica più: per cambiarla si crea una nuova versione. I dati del cliente
        restano quelli di oggi.
      </p>
      {canRegistry ? (
        <ClientPicker
          label="Cerca il cliente"
          value={clientId}
          onChange={setClientId}
          returnTo={`/preventivatore/${quote.id}`}
        />
      ) : (
        <p className="notice info">
          Serve l’accesso all’Anagrafica per scegliere il cliente: chiedi a chi ce l’ha.
        </p>
      )}
      <p className="muted small">Imponibile {formatMoney(quote.totals.net)}.</p>
    </ConfirmDialog>
  );
}

/** The discount of a line: the percentage, or what the amount written by hand took off. */
function LineDiscount({ line }: { line: QuoteLineResponse }) {
  if (line.finalAmount !== null) {
    const off = (parseDecimal(line.gross) ?? 0) - (parseDecimal(line.net) ?? 0);
    if (off <= 0) return <>—</>;
    return (
      <span title="Importo finale scritto a mano">
        −{formatMoney(off)}
        <small className="muted block">importo finale</small>
      </span>
    );
  }
  return <>{Number(line.discountPercent) === 0 ? '—' : formatPercent(line.discountPercent)}</>;
}

/**
 * The internal referent, changed from the header in any state (decision of
 * 01/10/2026): it is not part of what the client received; the history keeps
 * the change.
 */
/**
 * The commerciale di riferimento (decision of 05/10/2026): who follows the
 * opportunity and approves under the threshold. On a draft the commerciale,
 * or who sees every quote, picks another user who may write quotes.
 */
function OwnerPicker({
  quote,
  salespeople,
  onChanged,
}: {
  quote: QuoteResponse;
  salespeople: { id: string; name: string }[];
  onChanged: (updated: QuoteResponse) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const change = useMutation({
    mutationFn: (userId: string) =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}/owner`, { method: 'PUT', body: { userId } }),
    onSuccess: async (updated) => {
      setEditing(false);
      await onChanged(updated);
    },
  });

  if (editing) {
    return (
      <span className="owner-picker">
        commerciale di riferimento{' '}
        <select
          autoFocus
          aria-label="Commerciale di riferimento"
          value={quote.owner?.id ?? ''}
          disabled={change.isPending}
          onChange={(event) => event.target.value && change.mutate(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setEditing(false);
          }}
          onBlur={() => {
            if (!change.isPending) setEditing(false);
          }}
        >
          {!quote.owner && <option value="">—</option>}
          {salespeople.map((person) => (
            <option key={person.id} value={person.id}>
              {person.name}
            </option>
          ))}
          {quote.owner && !salespeople.some((person) => person.id === quote.owner!.id) && (
            <option value={quote.owner.id}>{quote.owner.name}</option>
          )}
        </select>
        {change.error && <span className="field-error"> {change.error.message}</span>}
      </span>
    );
  }
  return (
    <span className="owner-picker">
      commerciale di riferimento <strong>{quote.owner?.name ?? '—'}</strong>
      {quote.you.changeOwner && (
        <button
          type="button"
          className="link small"
          title="Cambia il commerciale di riferimento"
          aria-label="Cambia il commerciale di riferimento"
          onClick={() => setEditing(true)}
        >
          ✎
        </button>
      )}
    </span>
  );
}

function Totals({
  totals,
  lookups,
  onDiscount,
}: {
  totals: QuoteResponse['totals'];
  lookups: QuoteLookups;
  onDiscount?: (kind: DocumentDiscountKind) => void;
}) {
  const discounted = (parseDecimal(totals.documentDiscount) ?? 0) > 0;
  const set = (kind: DocumentDiscountKind) =>
    onDiscount && (
      <button type="button" className="link small" onClick={() => onDiscount(kind)}>
        imposta importo
      </button>
    );
  return (
    <dl className="totals">
      {discounted && (
        <>
          <div>
            <dt>Totale righe</dt>
            <dd>{formatMoney(totals.linesNet)}</dd>
          </div>
          <div className="discount">
            <dt>Sconto sul documento</dt>
            <dd>−{formatMoney(totals.documentDiscount)}</dd>
          </div>
        </>
      )}
      <div className="taxable">
        <dt>Imponibile {set('net')}</dt>
        <dd>{formatMoney(totals.net)}</dd>
      </div>
      {totals.vatByRate.map((rate) => (
        <div key={rate.code}>
          <dt>
            {rateLabel(lookups, rate.code)} su {formatMoney(rate.taxable)}
          </dt>
          <dd>{formatMoney(rate.vat)}</dd>
        </div>
      ))}
      <div className="grand">
        <dt>Totale {set('total')}</dt>
        <dd>{formatMoney(totals.total)}</dd>
      </div>
      {totals.target && !totals.target.exact && (
        <p className="muted small">
          {totals.target.kind === 'total' ? 'Il totale' : 'L’imponibile'} chiesto (
          {formatMoney(totals.target.amount)}) non si raggiunge al centesimo per l’arrotondamento
          dell’IVA: è il più vicino possibile.
        </p>
      )}
    </dl>
  );
}

/**
 * The taxable amount or the total the user wants (decision of 26/09/2026):
 * the difference becomes a discount spread on the lines, never on the premium.
 */
function DocumentDiscountDialog({
  quote,
  lookups,
  kind: initialKind,
  onClose,
  onDone,
}: {
  quote: QuoteResponse;
  lookups: QuoteLookups;
  kind: DocumentDiscountKind;
  onClose: () => void;
  onDone: (updated: QuoteResponse, removed: boolean) => Promise<void>;
}) {
  const [kind, setKind] = useState<DocumentDiscountKind>(
    quote.documentDiscount?.kind ?? initialKind,
  );
  const [amount, setAmount] = useState(
    quote.documentDiscount?.kind === initialKind ? quote.documentDiscount.amount : '',
  );
  const [error, setError] = useState('');
  const rates = Object.fromEntries(lookups.vatRates.map((rate) => [rate.code, rate.rate]));
  const lines = quote.lines.map((line) => ({
    editions: line.editions,
    quantity: line.quantity,
    unitPrice: line.unitPrice,
    discountPercent: line.discountPercent,
    finalAmount: line.finalAmount,
    premium: line.premium,
    vatCode: line.vatCode,
  }));
  const asked = parseDecimal(amount);
  const target = asked !== null && asked > 0 ? { kind, amount: asked } : null;
  const refusal = target ? documentDiscountError(lines, rates, target) : null;
  const preview = target && !refusal ? quoteTotals(lines, rates, target) : null;
  const plain = quoteTotals(lines, rates);

  const save = useMutation({
    mutationFn: (remove: boolean) =>
      remove
        ? apiRequest<QuoteResponse>(`/quotes/${quote.id}/document-discount`, { method: 'DELETE' })
        : apiRequest<QuoteResponse>(`/quotes/${quote.id}/document-discount`, {
            method: 'PUT',
            body: { kind, amount },
          }),
    onSuccess: (updated, remove) => onDone(updated, remove),
    onError: (failure: Error) => setError(failure.message),
  });

  return (
    <ConfirmDialog
      title="Sconto sul documento"
      confirmLabel="Applica"
      busy={save.isPending}
      error={error || refusal}
      onConfirm={() => {
        if (!target) setError('Scrivi l’importo che vuoi ottenere');
        else if (!refusal) save.mutate(false);
      }}
      onCancel={onClose}
    >
      <p className="muted small">
        Scrivi l’imponibile o il totale che vuoi: la differenza diventa uno sconto ripartito sulle
        righe, senza toccare il premium delle licenze. Se poi cambi le righe, lo sconto si toglie.
      </p>
      <div className="segmented-small" role="group" aria-label="Su cosa">
        <button
          type="button"
          className={kind === 'net' ? 'selected' : ''}
          onClick={() => setKind('net')}
        >
          Imponibile
        </button>
        <button
          type="button"
          className={kind === 'total' ? 'selected' : ''}
          onClick={() => setKind('total')}
        >
          Totale IVA inclusa
        </button>
      </div>
      <Field
        label={kind === 'net' ? 'Imponibile voluto' : 'Totale voluto'}
        hint={`Oggi ${formatMoney(kind === 'net' ? plain.net : plain.total)}`}
      >
        <DecimalInput
          className="number"
          decimals={[2, 2]}
          autoFocus
          value={amount}
          onChange={(value) => {
            setAmount(value);
            setError('');
          }}
        />
      </Field>
      {preview && (
        <dl className="totals">
          <div>
            <dt>Sconto sul documento</dt>
            <dd>−{formatMoney(preview.documentDiscount)}</dd>
          </div>
          <div>
            <dt>Imponibile</dt>
            <dd>{formatMoney(preview.net)}</dd>
          </div>
          <div>
            <dt>IVA</dt>
            <dd>{formatMoney(preview.vat)}</dd>
          </div>
          <div className="grand">
            <dt>Totale</dt>
            <dd>{formatMoney(preview.total)}</dd>
          </div>
          {preview.target && !preview.target.exact && (
            <p className="muted small">
              Per l’arrotondamento dell’IVA non si arriva al centesimo: questo è il più vicino.
            </p>
          )}
        </dl>
      )}
      {quote.documentDiscount && (
        <button
          type="button"
          className="link"
          disabled={save.isPending}
          onClick={() => save.mutate(true)}
        >
          Togli lo sconto sul documento
        </button>
      )}
    </ConfirmDialog>
  );
}

function rateLabel(lookups: QuoteLookups | undefined, code: string): string {
  return lookups?.vatRates.find((rate) => rate.code === code)?.description ?? code;
}
